import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { TableFilter } from '@trnsact/trnsact-shared-types';
import { APIRequestSort, TabsOption } from 'global';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { FinanceTable } from './FinanceTable';
import { FinanceSummaryCards } from './FinanceSummaryCards';
import { FinanceTableFilters } from './FinanceTableFilters';
import { FinanceTabsWrapper } from './FinanceTabsWrapper';
import { useFinanceListColumns } from '../../hooks/useFinanceListColumns';
import { useFinanceListFilters } from '../../hooks/useFinanceListFilters';
import {
  FinanceFilters,
  FinanceFilterType,
  calcFinanceFiltersAmount,
  formatFilterForFinancePayload,
} from 'modules/finance/filters';
import { FilterEvent } from 'components/shared/Filters';
import { SharedFinanceFilters, INITIAL_SHARED_FINANCE_FILTERS } from 'modules/finance/sharedFilters';
import { DateRangeBanner } from 'modules/finance/ui/components/DateRangeBanner';
import { convertFiltersToTabs } from '../../lib';
import { FinanceListTabs, FinanceListTabsType } from '../../types';
import { financeListApi } from '../../api';
import { useNotifications } from 'modules/notification';

interface Props {
  sharedFilters: SharedFinanceFilters;
  onSharedFiltersChange: (filters: SharedFinanceFilters) => void;
}

export const FinanceList = ({ sharedFilters, onSharedFiltersChange }: Props) => {
  const { showNotification } = useNotifications();
  const { columns } = useFinanceListColumns();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [currentTabValue, setCurrentTabValue] = useState<FinanceListTabsType | undefined>(undefined);
  const [sorting, setSorting] = useState<APIRequestSort>({ key: 'dealDate', value: 'desc' });
  const [isExportInProgress, setIsExportInProgress] = useState(false);
  const [localFilters, setLocalFilters] = useState<
    Pick<FinanceFilters, FinanceFilterType.SALES_REP | FinanceFilterType.LOCATION>
  >({
    [FinanceFilterType.SALES_REP]: [],
    [FinanceFilterType.LOCATION]: [],
  });

  const [aggregatedMetrics, setAggregatedMetrics] = useState({
    totalAftermarketProfit: 0,
    totalFIProfit: 0,
    productsSoldCount: 0,
  });

  const currentFilterConfig = useMemo<FinanceFilters>(
    () => ({
      [FinanceFilterType.DEAL_DATE]: sharedFilters.dateRange,
      [FinanceFilterType.DEAL_STATUS]: sharedFilters.dealStatus as FinanceFilters[FinanceFilterType.DEAL_STATUS],
      [FinanceFilterType.PAYMENT_TYPE]: sharedFilters.paymentType,
      [FinanceFilterType.SALES_REP]: localFilters[FinanceFilterType.SALES_REP],
      [FinanceFilterType.LOCATION]: localFilters[FinanceFilterType.LOCATION],
      [FinanceFilterType.DATE_TYPE]: sharedFilters.dateType,
    }),
    [sharedFilters, localFilters]
  );

  const onTableFiltersChanged = useCallback(
    (filters: Partial<FinanceFilters>, event: FilterEvent) => {
      if (event.type === 'apply') {
        onSharedFiltersChange({
          dateRange: filters[FinanceFilterType.DEAL_DATE] ?? sharedFilters.dateRange,
          dateType: filters[FinanceFilterType.DATE_TYPE] ?? sharedFilters.dateType,
          dealStatus: (filters[FinanceFilterType.DEAL_STATUS] ??
            sharedFilters.dealStatus) as SharedFinanceFilters['dealStatus'],
          paymentType: (filters[FinanceFilterType.PAYMENT_TYPE] ??
            sharedFilters.paymentType) as SharedFinanceFilters['paymentType'],
        });
        setLocalFilters({
          [FinanceFilterType.SALES_REP]: filters[FinanceFilterType.SALES_REP] ?? [],
          [FinanceFilterType.LOCATION]: filters[FinanceFilterType.LOCATION] ?? [],
        });
      }
      if (event.type === 'reset') {
        onSharedFiltersChange(INITIAL_SHARED_FINANCE_FILTERS);
        setLocalFilters({
          [FinanceFilterType.SALES_REP]: [],
          [FinanceFilterType.LOCATION]: [],
        });
      }
    },
    [sharedFilters, onSharedFiltersChange]
  );

  const handleTabChange = (tab: FinanceListTabsType) => {
    setCurrentTabValue(tab);
    const selectedFilter = tableFilters.find((f: TableFilter) => f.tableFilterId === tab);
    if (selectedFilter?.config) {
      onTableFiltersChanged(selectedFilter.config as Partial<FinanceFilters>, { type: 'apply' });
    } else {
      onTableFiltersChanged({}, { type: 'reset' });
    }
  };

  const {
    tableFilters,
    defaultFilter,
    handleFilterDelete,
    handleSetDefaultFilter,
    isFilterDeleting,
    isFiltersDataLoading,
    isSetDefaultTableFilterLoading,
  } = useFinanceListFilters((deletedFilter: TableFilter) => {
    if (currentTabValue === deletedFilter.tableFilterId) {
      setCurrentTabValue(FinanceListTabs.FinanceLog);
      onTableFiltersChanged({}, { type: 'reset' });
    }
  });

  useEffect(() => {
    if (isFiltersDataLoading) return;
    handleTabChange(defaultFilter?.tableFilterId ?? FinanceListTabs.FinanceLog);
  }, [isFiltersDataLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMetricsChange = useCallback(
    (newMetrics: {
      productsSoldCount: number;
      totalAftermarketProfit: number;
      totalFinanceInsuranceProfit: number;
    }) => {
      setAggregatedMetrics({
        productsSoldCount: newMetrics.productsSoldCount,
        totalAftermarketProfit: newMetrics.totalAftermarketProfit,
        totalFIProfit: newMetrics.totalFinanceInsuranceProfit,
      });
    },
    []
  );

  const handleExportFinanceLog = useCallback(async () => {
    setIsExportInProgress(true);
    try {
      await financeListApi.exportFinanceLog({
        sorting,
        filters: formatFilterForFinancePayload(currentFilterConfig),
      });
    } catch (error: unknown) {
      showNotification('Failed to export finance log CSV', { type: 'error' });
    } finally {
      setIsExportInProgress(false);
    }
  }, [sorting, currentFilterConfig, showNotification]);

  const filterTabs = convertFiltersToTabs(
    tableFilters,
    handleFilterDelete,
    columns,
    currentFilterConfig,
    handleMetricsChange
  );

  const standardTab: TabsOption<FinanceListTabsType> = {
    label: 'Finance Log',
    value: FinanceListTabs.FinanceLog,
    component: (
      <FinanceTable
        columns={columns}
        sorting={sorting}
        currentFilterConfig={currentFilterConfig}
        onSortingChange={setSorting}
        onAggregatedMetricsChange={handleMetricsChange}
      />
    ),
  };

  const tabs = [...filterTabs, standardTab];

  const filtersAmount = calcFinanceFiltersAmount(currentFilterConfig);

  return (
    <Box>
      <FinanceSummaryCards
        totalAftermarketProfit={aggregatedMetrics.totalAftermarketProfit}
        totalFIProfit={aggregatedMetrics.totalFIProfit}
        productsSoldCount={aggregatedMetrics.productsSoldCount}
      />

      <DateRangeBanner dateRange={sharedFilters.dateRange} dateType={sharedFilters.dateType} />

      <RenderWithCondition
        condition={currentTabValue !== undefined && !isFilterDeleting}
        fallBack={
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        }
      >
        <TabsContainer<FinanceListTabsType>
          tabs={tabs}
          initialTab={currentTabValue as FinanceListTabsType}
          onTabChange={handleTabChange}
          tabsWrapperProps={{ mb: 1 }}
          TabsLabelWrapper={({ children }) => (
            <FinanceTabsWrapper
              defaultFilter={defaultFilter}
              currentTabValue={currentTabValue!}
              onFiltersClick={() => setIsFiltersOpen(true)}
              onExportClick={handleExportFinanceLog}
              filtersAmount={filtersAmount}
              isExportInProgress={isExportInProgress}
              isSetDefaultTableFilterLoading={isSetDefaultTableFilterLoading}
              onSetAsDefaultClick={() => handleSetDefaultFilter(currentTabValue!)}
            >
              {children}
            </FinanceTabsWrapper>
          )}
        />
      </RenderWithCondition>

      <FinanceTableFilters
        isFiltersOpen={isFiltersOpen}
        listOfPresets={tableFilters}
        currentFilterConfig={currentFilterConfig}
        setIsFiltersOpen={setIsFiltersOpen}
        onTableFiltersChanged={onTableFiltersChanged}
      />
    </Box>
  );
};
